import React, { useState } from 'react';
import { 
  Box,
  Modal, 
  Paper,
  Typography,
  TextField, 
  Button, 
  Link,
  Alert, 
  IconButton, 
  InputAdornment,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Email as EmailIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { authService } from '../services/authService';

interface LoginModalProps {
  open: boolean;
  onClose: () => void;
  onSwitchToSignup: () => void;
  redirectPath?: string;
  onSuccess?: () => void;
  /** When true, show "Continue as guest" link (e.g. when booking a paid event). Focus stays on login. */
  showGuestCheckoutLink?: boolean;
  /** Called when user chooses guest checkout; parent should close login and open guest form. */
  onGuestCheckout?: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ 
  open, 
  onClose, 
  onSwitchToSignup,
  redirectPath = '/',
  onSuccess,
  showGuestCheckoutLink = false,
  onGuestCheckout,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const { login } = useAuth();
  const { showSnackbar } = useSnackbar();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState('');

  // Get redirect path from location state or props
  const getRedirectPath = () => {
    if (redirectPath && redirectPath !== '/') return redirectPath;
    if ((location.state as any)?.intendedPath) return (location.state as any).intendedPath;
    if ((location.state as any)?.from?.pathname) return (location.state as any).from.pathname;
    return '/';
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const from = getRedirectPath();
      await login(formData.email, formData.password, from);
      onClose();
      showSnackbar('Login successful!', 'success');
      // Call onSuccess callback if provided, with a small delay to ensure modal closes first
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 100);
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setError(
        error.response?.data?.message || 
        error.message || 
        'Failed to login. Please check your credentials.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ email: '', password: '' });
    setError(null);
    setShowPassword(false);
    setShowForgotPassword(false);
    setForgotPasswordEmail('');
    setForgotPasswordError('');
    setForgotPasswordSuccess('');
    setForgotPasswordLoading(false);
    onClose();
  };

  const handleSwitchToSignup = () => {
    onSwitchToSignup();
  };

  const handleGoogleSignIn = () => {
    authService.initiateGoogleAuth();
  };

  const handleForgotPasswordClick = () => {
    setShowForgotPassword(true);
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setError(null);
    setForgotPasswordError('');
    setForgotPasswordSuccess('');
    setForgotPasswordEmail('');
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotPasswordError('');
    setForgotPasswordSuccess('');
    setForgotPasswordLoading(true);

    try {
      await authService.forgotPassword(forgotPasswordEmail);
      setForgotPasswordSuccess('Password reset link has been sent to your email address.');
      setForgotPasswordEmail('');
    } catch (err: any) {
      setForgotPasswordError(err.response?.data?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <>
      <Modal 
        open={open} 
      onClose={handleClose}
        aria-labelledby="login-modal-title"
      aria-describedby="login-modal-description"
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        p: 0,
      }}
    >
      <Paper
        elevation={24}
        sx={{ 
          width: '100%',
          maxWidth: { xs: '100%', sm: '100%', md: '100%', lg: '100%', xl: '100%' },
          height: { xs: 'auto', sm: '75vh', md: '75vh', lg: '75vh', xl: '75vh' }, // Auto height on mobile, fixed 75vh on larger screens
          maxHeight: { xs: '90vh', sm: 'none', md: 'none', lg: 'none', xl: 'none' }, // Max height only on mobile
          overflow: { xs: 'visible', sm: 'auto', md: 'auto', lg: 'auto', xl: 'auto' }, // No scroll on mobile, auto scroll on larger screens
          borderRadius: 0,
          position: 'relative',
          bgcolor: '#5F3C6F', // Purple background matching header/footer
          boxShadow: theme.shadows[24],
          transform: open ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s ease-in-out',
        }}
      >

        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            width: 32,
            height: 32,
            borderRadius: 0,
            zIndex: 1,
            '&:hover': { 
              color: '#FF9DE7',
              backgroundColor: 'transparent',
            }
          }}
        >
          <CloseIcon />
        </IconButton>


        {/* Form */}
        <Box 
          component="form" 
          onSubmit={showForgotPassword ? handleForgotPasswordSubmit : handleSubmit} 
          sx={{ 
            p: { xs: 2, sm: 2 }, 
            pt: { xs: 3, sm: 3.5 },
            maxWidth: { xs: '90%', sm: '100%', md: 500, lg: 500, xl: 500 },
            mx: 'auto'
          }}
        >
          {showForgotPassword ? (
            // Forgot Password Form
            <>
              {forgotPasswordError && (
                <Alert severity="error" sx={{ mb: { xs: 1.5, sm: 2 } }}>
                  {forgotPasswordError}
                </Alert>
              )}
              {forgotPasswordSuccess && (
                <Alert severity="success" sx={{ mb: { xs: 1.5, sm: 2 } }}>
                  {forgotPasswordSuccess}
                </Alert>
              )}
            </>
          ) : (
            // Login Form
            <>
              {error && (
                <Alert severity="error" sx={{ mb: { xs: 1.5, sm: 2 } }}>
                  {error}
                </Alert>
              )}
            </>
          )}

          {showForgotPassword ? (
            // Forgot Password Email Field
            <TextField
              fullWidth
              placeholder="Email Address"
              name="forgotPasswordEmail"
              type="email"
              value={forgotPasswordEmail}
              onChange={(e) => setForgotPasswordEmail(e.target.value)}
              required
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: { xs: 1, sm: 1.5 },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  height: '56px', // Match button height
                  '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                  '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                  '&.Mui-focused fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.8)',
                  },
                },
              }}
            />
          ) : (
            // Login Form Fields
            <>
              {/* Email Field */}
              <TextField
                fullWidth
                placeholder="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: { xs: 1, sm: 1.5 },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    height: '56px', // Match button height
                    '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                    '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                    '&.Mui-focused fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.8)',
                    },
                  },
                }}
              />
              
              {/* Password Field */}
              <TextField
            fullWidth
            placeholder="Password"
            name="password"
                type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange}
            required
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
              ),
            }}
            sx={{ 
              mb: { xs: 1, sm: 1.5 },
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                height: '56px', // Match button height
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                },
              },
            }}
              />
            </>
          )}
            
          {/* Submit Button */}
            <Button
              type="submit"
            fullWidth
              variant="contained"
            size="large"
            disabled={showForgotPassword ? forgotPasswordLoading : loading}
              sx={{ 
              py: 1.2,
              mt: { xs: 2, sm: 3 },
              mb: { xs: 1, sm: 1.5 },
              borderRadius: 2,
              fontSize: '1rem',
                fontWeight: 'medium',
                textTransform: 'none',
              backgroundColor: '#FFD700', // Golden yellow
              color: '#000', // Black text
                '&:hover': {
                backgroundColor: '#FFC700', // Darker yellow on hover
              },
              '&:disabled': {
                backgroundColor: '#E0E0E0', // Gray when disabled
                color: '#9E9E9E', // Muted text when disabled
              },
            }}
          >
            {showForgotPassword 
              ? (forgotPasswordLoading ? 'Sending...' : 'Send Reset Link')
              : (loading ? 'Logging In...' : 'Login')
            }
          </Button>

          {!showForgotPassword && (
            <>
              {/* Divider */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 1.5 } }}>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'white' }} />
                <Typography variant="body2" color="white" sx={{ px: 2 }}>
                  OR
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'white' }} />
              </Box>

              {/* Google Login Button */}
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleGoogleSignIn}
                sx={{ 
                  py: 1.2,
                  mb: { xs: 1.5, sm: 2 },
                  borderRadius: 2,
                  fontSize: '1rem',
                  fontWeight: 'medium',
                  textTransform: 'none',
                  borderColor: '#dadce0',
                  color: '#3c4043',
                  backgroundColor: 'white',
                  '&:hover': {
                    borderColor: '#dadce0',
                    backgroundColor: '#f8f9fa',
                  },
                }}
              >
                <Box
                  component="img"
                  src="https://developers.google.com/identity/images/g-logo.png"
                  alt="Google"
                  sx={{ 
                    width: 20,
                    height: 20,
                    mr: 1,
                  }}
                />
                Continue with Google
                </Button>
            </>
          )}
            
          {/* Forgot Password / Back to Login Link */}
          <Box sx={{ textAlign: 'center', mb: { xs: 1.5, sm: 2 } }}>
            <Link
              component="button"
              onClick={showForgotPassword ? handleBackToLogin : handleForgotPasswordClick}
              variant="body2"
              sx={{ 
                color: '#FFD700', // Golden yellow
                textDecoration: 'none',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                  color: '#FFC700' // Darker yellow on hover
                }
              }}
            >
              {showForgotPassword ? 'Back to Login' : 'Forgot your password?'}
            </Link>
          </Box>

          {!showForgotPassword && (
            <>
              {/* Switch to Signup */}
              <Box sx={{ textAlign: 'center', pb: showGuestCheckoutLink ? 0.5 : { xs: 1.5, sm: 2 } }}>
                <Typography variant="body2" color="white" component="span">
                  Don't have an account?{' '}
                </Typography>
                <Link
                  component="button"
                variant="body2" 
                  onClick={handleSwitchToSignup}
                sx={{ 
                    color: '#FFD700', // Golden yellow
                    textDecoration: 'none',
                  '&:hover': {
                      textDecoration: 'underline',
                      color: '#FFC700' // Darker yellow on hover
                    },
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                  }}
              >
                Sign Up
                </Link>
              </Box>
              {/* Guest checkout: secondary option when booking a paid event */}
              {showGuestCheckoutLink && onGuestCheckout && (
                <Box sx={{ textAlign: 'center', pb: { xs: 1.5, sm: 2 } }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={onGuestCheckout}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.85)',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: 'inherit',
                    }}
                  >
                    Continue as guest
                  </Link>
                </Box>
              )}
            </>
          )}
        </Box>
      </Paper>
    </Modal>

  </>
  );
};

export default LoginModal;
